import { EmptyState } from '@workspace/ui/components/empty-state'
import { cn } from '@workspace/ui/lib/utils'
import { Bell } from 'lucide-react'
import { useState } from 'react'
import { trackEvent } from '../../../../lib/gtag'
import { copy } from '../../copy/investor-copy'
import {
  useFollowsQuery,
  useMarkNotificationsRead,
  useNotificationsQuery,
} from '../../hooks/use-investor-assistant'
import { sourceLabel } from './source-label'
import { StartupTile } from './startup-tile'

type Item = NonNullable<ReturnType<typeof useNotificationsQuery>['data']>['data']['items'][number]
type Filter = 'all' | 'unread'

const DAY_MS = 24 * 60 * 60 * 1000

const GROUPS = ['Today', 'Yesterday', 'This week', 'Earlier'] as const
type Group = (typeof GROUPS)[number]

function groupOf(date: Date): Group {
  const startOfToday = new Date()
  startOfToday.setHours(0, 0, 0, 0)
  const age = startOfToday.getTime() - date.getTime()
  if (age <= 0) return 'Today'
  if (age <= DAY_MS) return 'Yesterday'
  if (age <= 6 * DAY_MS) return 'This week'
  return 'Earlier'
}

/** "09:40" for today, "10 Oct" otherwise. */
function whenText(date: Date): string {
  return groupOf(date) === 'Today'
    ? date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })
    : date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })
}

function isConditionMet(item: Item): boolean {
  return item.statements.some((s) => s.conditionKey === 'watch')
}

/** The bell entry for a founder who joined through the investor's invite: no quote, a line instead. */
const isInviteJoined = (item: Item): boolean => item.type === 'FOUNDER_INVITE_JOINED'

function UpdateRow({ item, onOpen }: { item: Item; onOpen: (startupId: string) => void }) {
  const markRead = useMarkNotificationsRead()
  const created = new Date(item.createdAt)
  const unread = !item.readAt
  const conditionMet = isConditionMet(item)
  const inviteJoined = isInviteJoined(item)
  const name = item.startupName ?? 'A startup'
  const first = item.statements[0]

  const open = () => {
    // Opening an entry reads it, wherever it is listed.
    if (unread) {
      trackEvent('assistant_alerts_marked_read', { scope: 'one' })
      markRead.mutate({ body: { ids: [item.id] } })
    }
    if (!item.startupId) return
    trackEvent('assistant_startup_link_clicked', { surface: 'alerts', startup_id: item.startupId })
    onOpen(item.startupId)
  }

  return (
    <li>
      <button
        type="button"
        onClick={open}
        className={cn(
          'relative flex w-full items-start gap-3 py-3 pl-7 pr-4 text-left transition-colors hover:bg-muted/30',
          !item.startupId && !unread && 'cursor-default'
        )}
      >
        {unread && (
          <span
            role="img"
            aria-label="Unread update"
            className="absolute left-[11px] top-1/2 size-1.5 -translate-y-1/2 rounded-full bg-[#f97316]"
          />
        )}
        <StartupTile name={name} />
        <span className="w-44 shrink-0">
          <span className="block font-medium text-foreground">{name}</span>
          <span
            className={cn(
              'flex items-center gap-1 text-xs',
              conditionMet ? 'font-medium text-emerald-500' : 'text-muted-foreground'
            )}
          >
            <Bell className="size-3" />
            {inviteJoined
              ? copy('notify.joinedInvite.label')
              : conditionMet
                ? 'Condition met'
                : 'Material update'}
          </span>
        </span>
        <span className="min-w-0 flex-1 space-y-1 text-sm text-foreground">
          {inviteJoined && <span className="block">{copy('notify.joinedInvite.body')}</span>}
          {item.statements.map((statement) => (
            <span key={`${statement.conditionKey}-${statement.quote}`} className="block">
              “{statement.quote}”
            </span>
          ))}
        </span>
        <span className="flex shrink-0 items-center gap-4 text-xs text-muted-foreground">
          <span className="min-w-0 max-w-[16rem] truncate" title={first?.source}>
            {first ? sourceLabel(first.source) : ''}
          </span>
          <span className="w-12 text-right">{whenText(created)}</span>
        </span>
      </button>
    </li>
  )
}

function FilterChip({
  label,
  count,
  active,
  onClick,
}: {
  label: string
  count: number
  active: boolean
  onClick: () => void
}) {
  return (
    <button
      type="button"
      aria-pressed={active}
      onClick={onClick}
      className={cn(
        'rounded-full px-3 py-1 text-xs transition-colors',
        active
          ? 'bg-muted font-medium text-foreground'
          : 'text-muted-foreground hover:text-foreground'
      )}
    >
      {label} <span className="ml-0.5 opacity-70">{count}</span>
    </button>
  )
}

/** What the followed startups said that the investor asked for, newest first, grouped by day. */
export function UpdatesTab({ onOpen }: { onOpen: (startupId: string) => void }) {
  const inbox = useNotificationsQuery()
  const follows = useFollowsQuery()
  const markRead = useMarkNotificationsRead()
  const [filter, setFilter] = useState<Filter>('all')
  // Only what the investor still follows: a stopped follow's updates leave this list.
  const following = new Set(
    (follows.data?.data ?? [])
      .filter((f) => f.status !== 'ENDED_BY_INVESTOR')
      .map((f) => f.startupId)
  )
  const all = (inbox.data?.data.items ?? []).filter(
    (item) => item.startupId !== null && following.has(item.startupId)
  )
  const unread = all.filter((item) => !item.readAt).length
  const shown = filter === 'unread' ? all.filter((item) => !item.readAt) : all

  const byGroup = new Map<Group, Item[]>()
  for (const item of shown) {
    const group = groupOf(new Date(item.createdAt))
    byGroup.set(group, [...(byGroup.get(group) ?? []), item])
  }

  return (
    <div className="space-y-5">
      <div className="space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-1">
            <FilterChip
              label="All"
              count={all.length}
              active={filter === 'all'}
              onClick={() => setFilter('all')}
            />
            <FilterChip
              label="Unread"
              count={unread}
              active={filter === 'unread'}
              onClick={() => setFilter('unread')}
            />
          </div>
          {unread > 0 && (
            <button
              type="button"
              disabled={markRead.isPending}
              onClick={() => {
                trackEvent('assistant_alerts_marked_read', { scope: 'all' })
                markRead.mutate({ body: {} })
              }}
              className="text-xs text-muted-foreground hover:text-foreground disabled:cursor-default disabled:opacity-50"
            >
              Mark all as read
            </button>
          )}
        </div>
        <p className="text-xs text-muted-foreground">
          We email you what you asked for the day it’s posted. Everything else your startups share
          waits here.
        </p>
      </div>

      {shown.length === 0 ? (
        <EmptyState
          title={filter === 'unread' ? 'Nothing unread' : 'Nothing yet'}
          description="Alerts will appear here."
        />
      ) : (
        GROUPS.filter((group) => byGroup.has(group)).map((group) => (
          <section key={group} className="space-y-2">
            <h2 className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
              {group}
            </h2>
            <ul className="divide-y divide-border overflow-hidden rounded-xl border border-border">
              {(byGroup.get(group) ?? []).map((item) => (
                <UpdateRow key={item.id} item={item} onOpen={onOpen} />
              ))}
            </ul>
          </section>
        ))
      )}
    </div>
  )
}
